import Resource from 'Resources/Resource';
import AppService from 'App/legacy/AppService';
import logError from 'Helpers/logError';
import { TYPE, STYLE } from 'Store/reducers/SystemNotificationReducer';
import ScriptType from './Model/Script';

class Script extends Resource {
	eventName = 'script';

	constructor() {
		super('uiScript');

		/*
			What ResourceHelper actually produced for this resource, spelled out. The base's
			getDefaultNotifications only fires when the constructor is given a notificationTitle,
			and passing one would generate copy Angular never showed (the same trap documented in
			AccountProfile.ts). These are ResourceHelper's own defaults merged with the overrides
			upsales/common/resources/scripts.js set: it defined no updateSuccessBody, so the update
			body is 'default.updated' rather than an 'updated.script' that never existed.

			delete/deleteError are not needed — AdminScripts.tsx already deletes through this
			resource without them.
		*/
		// opts is optional-chained because the base calls notifications.save() with no arguments on
		// its dontWait path, outside the try/catch that guards _handleNotification.
		this.notifications = {
			save: (_data: unknown, opts?: { isNew?: boolean }) => ({
				title: opts?.isNew ? 'default.saved' : 'default.updated',
				body: opts?.isNew ? 'saved.script' : 'default.updated',
				style: STYLE.SUCCESS,
				icon: 'check',
				type: TYPE.BODY
			}),
			saveError: (_data: unknown, opts?: { isNew?: boolean }) => ({
				title: 'default.error',
				body: opts?.isNew ? 'saveError.script' : 'default.error',
				style: STYLE.ERROR,
				icon: 'times',
				type: TYPE.BODY
			})
		} as any;
	}

	find(...args: any): Promise<{ data: ScriptType[] }> {
		return super.find(...args);
	}

	new(): ScriptType {
		return {
			description: '',
			active: true,
			type: 'order_edit',
			code:
				'// use the relevant object to manipulate data,\n' +
				'// which object that exists depends on when the scripts run (order, account or contact)\n' +
				"// e.g., order.description = 'Hello' to set description  \n" +
				' \n' +
				"// on 'save' scripts, a callback cb() function has to be called, if you don't \n" +
				'// want the object to be saved send an error message as the parameter \n' +
				"// e.g., return cb('the order didn't validate'); \n" +
				' \n' +
				'//jQuery/$, async, moment and some other js libraries are available\n' +
				' \n' +
				'// upsales.me is an object containing info of current user \n' +
				'// upsales.get[type](id, cb) gets an object, \n' +
				'//e.g., upsales.get.account(123, function(err, account) {...}) \n' +
				'// upsales.open[type](id, cb) navigates within the app, \n' +
				'//e.g., upsales.open.account(123) \n' +
				'// upsales.notify(msg) shows a notification \n' +
				'// upsales.warning(msg) shows an alert \n'
		};
	}

	/*
		Angular refetched the list and pushed it into App.scripts after every successful
		create/update. That is load-bearing rather than cosmetic: App.scripts is read live by
		App/services/Modal/shouldOpenModal.ts, App/components/EditOrder, the Salesboard drag-drop
		hooks and AdminScript itself, so without the refresh a newly created or newly activated
		script does not take effect until a page reload.

		The refresh must not fail the save: super.save() has already written the row and fired the
		"Saved" toast by this point, so letting a failed GET reject would leave the caller on the
		edit page next to a success toast for a write that landed. Angular dropped the refetch
		promise entirely, which had the same effect while also losing the error; logging it keeps
		that behaviour without swallowing anything. Awaiting costs the save one extra round trip.
	*/
	async save(script: ScriptType, opts = {}) {
		const res = await super.save(script, opts);
		try {
			const listRes = await this.find({});
			AppService.setScripts(listRes.data);
		} catch (e) {
			logError(e, 'Could not refresh App.scripts after saving a script');
		}
		return res;
	}
}

const resource = new Script();

export default resource;
